import { Component, OnInit, Output, EventEmitter, Inject, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { NavController } from '@ionic/angular';
import { states } from '@ewarren/unitedstatesofamerica';

/** ISO 3166-1 alpha-2 country codes for the address form (country-first, international-friendly). */
export const COMPANY_COUNTRIES: { code: string; name: string }[] = [
  { code: '', name: '' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'MX', name: 'Mexico' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'OTHER', name: 'Other' }
];

@Component({
  selector: 'app-account-settings-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss'],
})
export class AccountSettingsCompanyComponent implements OnInit {
  @Output() pending = new EventEmitter<Object>();
  @Output() buttonText = new EventEmitter<String>();

  public companyForm: UntypedFormGroup;
  public statesMappings: { abbreviation: string; name: string }[];
  public updatePending = false;
  readonly countries = COMPANY_COUNTRIES;

  constructor(public accountService: AccountService,
    private navCtrl: NavController,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean
  ) {
    this.createForm();
    this.statesMappings = Object.keys(states).map((s) => ({ abbreviation: s, name: states[s] }));
  }

  ngOnInit() {
    if (this.accountInformation) {
      this.createForm();
    }
    this.statesMappings = Object.keys(states).map((s) => ({ abbreviation: s, name: states[s] }));
  }

  /** True when the selected country is US (show state dropdown and "Zip Code"). */
  isUS(): boolean {
    const country = this.companyForm?.get('country')?.value;
    return country === 'US';
  }

  async successfulUpdate() {
    this.updatePending = false;
    this.pending.emit({
      is_pending: false,
      message: 'Company successfully updated',
      success: true
    });
  }

  createForm() {
    if (!this.companyForm && this.accountInformation) {
      const company_info = this.accountInformation?.company;
      const account_owner_info = this.accountInformation?.account_owner;
      const address = company_info?.address;

      const name = new UntypedFormControl(company_info?.name ?? '');
      const country = new UntypedFormControl(address?.country ?? 'US');
      const line_one = new UntypedFormControl(address?.line_one ?? '');
      const line_two = new UntypedFormControl(address?.line_two ?? '');
      const city = new UntypedFormControl(address?.city ?? '');
      const state = new UntypedFormControl(address?.state ?? '');
      const postal_code = new UntypedFormControl(address?.postal ?? '');
      const first_name = new UntypedFormControl(account_owner_info?.name?.first_name ?? '');
      const last_name = new UntypedFormControl(account_owner_info?.name?.last_name ?? '');

      this.companyForm = new UntypedFormGroup({
        name,
        weekly_summary: new UntypedFormControl(false),
        country,
        line_one,
        line_two,
        city,
        state,
        postal_code,
        first_name,
        last_name
      });
    }
  }

  getStates() {
    return this.statesMappings;
  }

  public save() {
    const form_value = this.companyForm.value;
    const countryCode = form_value.country === 'OTHER' ? '' : (form_value.country || '');
    this.accountService.updateCompanyInfo(this.accountInformation.general.external_id, form_value.name, form_value.weekly_summary, {
      line_one: form_value.line_one,
      line_two: form_value.line_two,
      city: form_value.city,
      state: form_value.state,
      postal: form_value.postal_code,
      country: countryCode
    }, {
      first_name: form_value.first_name,
      last_name: form_value.last_name
    })
        .then((data) => { this.successfulUpdate(); })
        .catch((error) => {
          this.updatePending = false;
          this.pending.emit({
            is_pending: false,
            message: 'Sorry we could not update the company information at this time.',
            success: true
          });
        })

    this.updatePending = true;
  }

  public isValid() {
    // With all-optional fields, the form is valid as soon as it exists
    return !!this.companyForm && this.companyForm.valid;
  }

  goToSwitchAccounts() {
    this.navCtrl.navigateForward('/switch-account');
  }
}
